// src/lib/resources/utils/resourceUtils.ts

/**
 * Client-side utility functions for working with resources
 */

import type { SupplementalResourceReference } from '~/lib/resources/types/shared/base'
import type { GlossaryTerm } from '~/lib/resources/types/glossary/terms'

//=============================================================================
// URL Utility Functions
//=============================================================================

/**
 * Check if a URL is absolute
 * 
 * @param {string} url - URL to check
 * @returns {boolean} True if URL is absolute
 */
export function isAbsoluteUrl(url: string): boolean {
  if (!url) return false
  return /^(?:[a-z+]+:)?\/\//i.test(url)
}


//=============================================================================
// SupplementalResource Functions (for working with SupplementalResourceReference)
//=============================================================================

/**
 * Gets the full URL from a supplemental resource
 * Handles different formats of URLs for internal vs external resources
 * 
 * @param {SupplementalResourceReference} resource - The supplemental resource
 * @returns {string} The complete URL
 */
export function getUrlFromSupplementalResource(resource: SupplementalResourceReference): string {
  if (!resource) {
    return ''
  }

  // If the resource already has a complete URL, use it
  if (resource.url) {
    return resource.url
  }
  
  // Check if it's an internal blog resource and use specialized function
  if (resource.isInternal === true && 
      (resource.learningResourceType === 'article' || 
       resource.learningResourceType === 'article-collection' || 
       resource.blogTopic)) {
    return getBlogUrl(resource)
  }
  
  // For internal resources, ensure proper formatting with leading slash
  if (resource.isInternal === true) {
    // Get the slug from urlSlug or fall back to slug
    let internalPath = resource.urlSlug?.trim() ?? resource.slug?.trim() ?? ''
    
    // Ensure internal paths start with a slash
    if (internalPath && !internalPath.startsWith('/')) {
      internalPath = `/${internalPath}`
    }
    
    return internalPath
  }
  
  // For external resources, combine baseUrl and urlSlug
  if (resource.baseUrl) {
    // Clean and normalize the base URL
    let baseUrl = resource.baseUrl.trim()
    
    // Ensure baseUrl has a protocol
    if (!baseUrl.startsWith('http://') && !baseUrl.startsWith('https://')) {
      baseUrl = `https://${baseUrl}`
    }
    
    // Remove trailing slashes from baseUrl
    while (baseUrl.endsWith('/')) {
      baseUrl = baseUrl.slice(0, -1)
    }

    // Clean and normalize the urlSlug
    let urlSlug = resource.urlSlug?.trim() ?? resource.slug?.trim() ?? ''
    
    // Remove leading slashes from urlSlug
    while (urlSlug.startsWith('/')) {
      urlSlug = urlSlug.substring(1)
    }

    // Combine with a single slash
    return urlSlug ? `${baseUrl}/${urlSlug}` : baseUrl
  }
  
  // If we have a urlSlug but no baseUrl and it's not explicitly internal,
  // format it properly (could be a relative path)
  let fallbackPath = resource.urlSlug?.trim() ?? resource.slug?.trim() ?? ''
  
  // For non-absolute paths that don't start with a slash, add one
  if (fallbackPath && !fallbackPath.startsWith('/') && !isAbsoluteUrl(fallbackPath)) {
    fallbackPath = `/${fallbackPath}`
  }
  
  return fallbackPath
}

/**
 * Check if a supplemental resource is external
 * 
 * @param {SupplementalResourceReference} resource - The supplemental resource to check
 * @returns {boolean} True if the resource is external
 */
export function isExternalSupplementalResource(resource: SupplementalResourceReference): boolean {
  // First check if the resource has an explicit isInternal property
  if (typeof resource.isInternal === 'boolean') {
    return !resource.isInternal
  }
  
  // Next check if the resource has a url property with an external domain
  if (resource.url) {
    return resource.url.startsWith('http://') || resource.url.startsWith('https://')
  }
  
  // Finally check if baseUrl is an external domain
  if (resource.baseUrl) {
    return resource.baseUrl.startsWith('http://') || resource.baseUrl.startsWith('https://')
  }
  
  // Default to internal if no clear indicators
  return false
}

/**
 * Format a display name for a supplemental resource
 * 
 * @param {SupplementalResourceReference} resource - The supplemental resource
 * @returns {string} Formatted display name
 */
export function formatSupplementalResourceDisplayName(resource: SupplementalResourceReference): string {
  // First check if resource is internal
  const isExternal = isExternalSupplementalResource(resource)
  
  // For internal resources, show resource type
  if (!isExternal) {
    // First, use explicitly typed resources
    if (resource.learningResourceType) {
      // Format the resource type for display (convert-kebab-case to Title Case)
      const formattedType = resource.learningResourceType
        .split(/[-_]/)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
      
      if (formattedType === 'Glossary Term') {
        return 'Glossary Term'
      }
      if (formattedType === 'Ebook') {
        return 'eBook'
      }
      if (formattedType === 'Case Study') {
        return 'Case Study'
      }
      
      return `AdSights ${formattedType}`
    }
    // Fall back to generic "Resource" if type isn't specified
    return 'Resource'
  }
  
  // For external resources, show domain name
  if (resource.url) {
    try {
      const url = new URL(resource.url)
      return url.hostname.replace('www.', '')
    } catch (_e) {
      // If URL parsing fails, fall back to baseUrl
    }
  }
  
  if (resource.baseUrl) {
    try {
      // Try to extract domain from baseUrl
      const baseUrl = resource.baseUrl.startsWith('http') 
        ? resource.baseUrl 
        : `https://${resource.baseUrl}`
      const url = new URL(baseUrl)
      return url.hostname.replace('www.', '')
    } catch (_e) {
      // If URL parsing fails, just return the baseUrl
      return resource.baseUrl
    }
  }
  
  // Fall back to generic label if all else fails
  return 'External Resource'
}

//=============================================================================
// Legacy Resource Functions (for compatibility with ExternalResource)
//=============================================================================

/**
 * @deprecated 
 * Kept only for TypeScript compatibility. This function no longer performs any actual check, 
 * as it just redirects to hasSupplementalResources. Use hasSupplementalResources directly.
 */
export function hasExternalResources(term: GlossaryTerm): boolean {
  // Redirect to new implementation
  return hasSupplementalResources(term)
} 

/**
 * Check if a glossary term has supplemental resources
 * 
 * @param {GlossaryTerm} term - The glossary term to check
 * @returns {boolean} True if the term has supplemental resources
 */
export function hasSupplementalResources(term: GlossaryTerm): boolean {
  return Boolean(
    'supplementalResources' in term &&
      Array.isArray(term.supplementalResources) &&
      term.supplementalResources.length > 0,
  )
}

/**
 * Get properly formatted blog URL based on resource type
 * 
 * This function intelligently determines if the resource is a blog article or
 * a collection (topic, author, tag, archive) and formats the URL accordingly.
 * 
 * @param {SupplementalResourceReference} resource - Resource representing a blog item
 * @returns {string} Properly formatted blog URL
 */
export function getBlogUrl(resource: SupplementalResourceReference): string {
  if (!resource || resource.isInternal !== true) {
    return getUrlFromSupplementalResource(resource)
  }
  
  // Get the base slug
  const slug = resource.urlSlug ?? resource.slug ?? ''
  
  // Ensure slug starts with a slash
  const normalizedSlug = slug.startsWith('/') ? slug : `/${slug}`
  
  // If the slug already contains the full path starting with /blog, use it
  if (normalizedSlug.startsWith('/blog/')) {
    return normalizedSlug
  }
  
  // Otherwise, construct the path based on resource type
  const isArticleCollection = resource.learningResourceType === 'article-collection'
  const isArticle = resource.learningResourceType === 'article'
  
  // Check if the path contains collection identifiers
  const hasCollectionPath = /\/(topics|tags|authors|archive)\//.test(normalizedSlug)
  
  // For article collections, ensure proper format: /blog/{collection-type}/{collection-slug}
  if (isArticleCollection) {
    if (hasCollectionPath) {
      return normalizedSlug.startsWith('/blog/') ? normalizedSlug : `/blog${normalizedSlug}`
    } else {
      // Default to topics if no collection type specified
      return `/blog/topics${normalizedSlug}`
    }
  }
  
  // For articles, ensure proper format: /blog/{collection-type}/{collection-slug}/{article-slug}
  if (isArticle) {
    // If path already has collection identifiers, just ensure it starts with /blog
    if (hasCollectionPath) {
      return normalizedSlug.startsWith('/blog/') ? normalizedSlug : `/blog${normalizedSlug}`
    } else {
      // If no collection path is specified, we assume it's under topics
      const pathSegments = normalizedSlug.split('/').filter(Boolean)
      
      // If there's only one segment, it's likely just the article slug without a topic
      if (pathSegments.length <= 1) {
        if (process.env.NODE_ENV === 'development') {
          console.warn(`resourceUtils: Blog article URL missing topic path: ${normalizedSlug}`)
        }
        return `/blog/topics${normalizedSlug}`
      }
      
      // Otherwise, format as /blog/topics/{remaining-path}
      return `/blog/topics${normalizedSlug}`
    }
  }
  
  // Default fallback - just ensure it starts with /blog
  return normalizedSlug.startsWith('/blog/') ? normalizedSlug : `/blog${normalizedSlug}`
}